import i18next from "i18next";

import {fullFetch} from "./api";
import urls, {localizeAppPageUrl} from "./urls";
import {escapeHtml} from "./html";
import {
    createOlxCategoryPicker,
    normalizeCategoryPickerCategories,
} from "./olx-category-picker";
import {
    normalizeCollection,
    renderDraftSearchableSelect,
} from "./olx-category-location-controls";
import {
    bindDateTimePickerEvents,
    dateTimePickerHtml,
    isDateTimePickerValueInPast,
    setDateTimePickerValue,
} from "./date-time-picker";
import {
    formatMoney,
    getDefaultReusablePlacementPacket,
    getPlacementPacketKey,
    getPreferredReusablePlacementPacketForSize,
    getReusablePackagePurchaseOptions,
    renderBoughtPacketsList,
    renderReusablePackageSelector,
} from "./olx-placement-package-ui";
import {getDelayedOperationQuotaStatus} from "./delayed-operation-quota";

const t = (key, fallback, options = {}) => (
    i18next.t ? i18next.t(key, {defaultValue: fallback, ...options}) : fallback
);

const ACCOUNT_RESOURCE_FETCH_TIMEOUT_MS = 30000;

const extractDelayedOperationQuota = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const source = payload.errors && typeof payload.errors === 'object' ? payload.errors : payload;
    const value = source.delayed_operation_quota;
    if (Array.isArray(value)) {
        return value.find((item) => item && typeof item === 'object') || null;
    }
    if (value && typeof value === 'object') {
        return value;
    }
    return null;
};

const formatDelayedOperationQuotaError = (quota) => (
    t(
        'ads:placementPlan.delayedOperationsLimitExceeded',
        'Not enough delayed actions available. Available: {{remaining}}, selected: {{requested}}.',
        {
            requested: Number(quota?.requested || 0),
            remaining: Number(quota?.remaining || 0),
        },
    )
);

const stringifyErrorValue = (value) => {
    if (!value) {
        return '';
    }
    if (value instanceof Error) {
        return value.message;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(stringifyErrorValue).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return String(value);
        }
    }
    return String(value);
};

const parseEncodedJsonArray = (value) => {
    try {
        const parsed = JSON.parse(decodeURIComponent(String(value || '')));
        return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch (error) {
        return [];
    }
};

const formatErrors = (errors) => {
    if (!errors) {
        return '';
    }
    const quota = extractDelayedOperationQuota(errors);
    if (quota) {
        return formatDelayedOperationQuotaError(quota);
    }
    if (errors instanceof Error) {
        return errors.message;
    }
    if (errors.errors && typeof errors.errors === 'object') {
        return formatErrors(errors.errors);
    }
    if (typeof errors === 'string') {
        return errors;
    }
    if (Array.isArray(errors)) {
        return errors.map(stringifyErrorValue).filter(Boolean).join(', ');
    }
    if (typeof errors === 'object') {
        return Object.values(errors).flat().map(stringifyErrorValue).filter(Boolean).join(', ')
            || stringifyErrorValue(errors);
    }
    return String(errors);
};

const normalizePlacementAccount = (account) => {
    if (!account || typeof account !== 'object') {
        return null;
    }
    const id = String(account.id || account.account_id || '').trim();
    if (!id) {
        return null;
    }
    const status = String(account.status || 'WORKING').toUpperCase();
    const activeValue = account.is_active ?? account.active ?? account.enabled ?? true;
    const isActive = activeValue !== false && String(activeValue).toLowerCase() !== 'false';
    return {
        id,
        name: String(account.name || account.account_name || account.login || id),
        status,
        is_active: isActive,
    };
};

const createRequestTimeout = (timeoutMs = ACCOUNT_RESOURCE_FETCH_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    return {
        signal: controller.signal,
        clear: () => window.clearTimeout(timer),
    };
};

export const createOlxAccountPackagesModalController = ({
    getAvailableAccounts = () => [],
    getRequestHeaders = () => ({}),
} = {}) => {
    const state = {
        balanceByAccountId: new Map(),
        balancePromisesByAccountId: new Map(),
        resourcesByAccountId: new Map(),
        selectedAccountId: '',
        selectedCategoryId: '',
        selectedZoneId: '',
        selectedPaymentMethod: 'account',
        selectedPacketKey: '',
        preview: null,
        categories: [],
        categoriesLoading: false,
        resourcesLoading: new Set(),
        resourcePromisesByAccountId: new Map(),
        resourceErrorsByAccountId: new Map(),
        previewLoading: false,
        purchaseLoading: false,
        mode: 'purchase',
        scheduledAt: '',
        maxPriceAmount: '',
        scheduleLoading: false,
        delayedOperationQuota: null,
        isSyncingDatePicker: false,
        locationTimer: null,
        selectedLocationLabel: '',
        categoryPromise: null,
        expandedPackageSizeKeys: [],
        autoSelectAccount: true,
    };

    const getDatePickerLabels = () => ({
        today: t('common:today', 'Today'),
        clear: t('common:clear', 'Clear'),
        done: t('common:done', 'Done'),
        previousMonth: t('common:previousMonth', 'Previous month'),
        nextMonth: t('common:nextMonth', 'Next month'),
        hour: t('common:hour', 'Hour'),
        minute: t('common:minute', 'Minute'),
    });

    const categoryPicker = createOlxCategoryPicker({
        id: 'olx-account-package-category-picker',
        onSelect: (categoryId) => {
            state.selectedCategoryId = String(categoryId || '');
            state.preview = null;
            state.selectedPacketKey = '';
            loadPackagePreview();
        },
    });

    const getAvailablePlacementAccounts = () => {
        const seen = new Set();
        return (getAvailableAccounts() || []).reduce((accounts, rawAccount) => {
            const account = normalizePlacementAccount(rawAccount);
            if (!account || !account.is_active || account.status !== 'WORKING' || seen.has(account.id)) {
                return accounts;
            }
            seen.add(account.id);
            accounts.push(account);
            return accounts;
        }, []);
    };

    const getAvailablePlacementAccount = (accountId) => {
        const normalizedId = String(accountId || '');
        return getAvailablePlacementAccounts().find((account) => String(account.id) === normalizedId) || null;
    };

    const ensureSelectedPlacementAccount = () => {
        const accounts = getAvailablePlacementAccounts();
        if (!accounts.length) {
            state.selectedAccountId = '';
            return null;
        }
        const selected = accounts.find((account) => String(account.id) === String(state.selectedAccountId || ''));
        if (selected) {
            return selected;
        }
        if (!state.autoSelectAccount && accounts.length > 1) {
            state.selectedAccountId = '';
            return null;
        }
        state.selectedAccountId = String(accounts[0].id);
        return accounts[0];
    };

    const syncDelayedOperationQuotaFromPayload = (payload, accountId = state.selectedAccountId) => {
        const quota = extractDelayedOperationQuota(payload) || payload?.delayed_operation_quota || null;
        if (!quota || typeof quota !== 'object') {
            return null;
        }
        state.delayedOperationQuota = quota;
        const accountKey = String(accountId || '');
        if (accountKey && state.resourcesByAccountId.has(accountKey)) {
            state.resourcesByAccountId.set(accountKey, {
                ...state.resourcesByAccountId.get(accountKey),
                delayed_operation_quota: quota,
            });
        }
        return quota;
    };

    const getSelectedDelayedOperationQuota = () => {
        const accountId = String(state.selectedAccountId || '');
        return state.resourcesByAccountId.get(accountId)?.delayed_operation_quota
            || state.delayedOperationQuota
            || null;
    };

    const getScheduleQuotaState = (quota = getSelectedDelayedOperationQuota()) => (
        getDelayedOperationQuotaStatus(quota, {requested: Number(quota?.requested || 1)})
    );

    const isDelayedOperationQuotaExhausted = (quota = getSelectedDelayedOperationQuota()) => {
        const status = getScheduleQuotaState(quota)?.status;
        return ['exhausted', 'exceeded'].includes(status);
    };

    const getScheduleQuotaError = () => {
        const quota = getScheduleQuotaState();
        return quota && ['exhausted', 'exceeded'].includes(quota.status)
            ? formatDelayedOperationQuotaError(quota)
            : '';
    };

    const renderAccountSelectControl = ({id, dataAttributes = ''}) => {
        const accounts = getAvailablePlacementAccounts();
        if (!accounts.length) {
            return '';
        }
        return renderDraftSearchableSelect({
            id,
            label: t('ads:placementAccounts.olxAccount', 'OLX account'),
            options: accounts.map((account) => ({
                value: account.id,
                label: account.name,
            })),
            selectedValue: state.selectedAccountId,
            placeholder: t('ads:placementAccounts.olxAccount', 'OLX account'),
            searchPlaceholder: t('ads:draftEditor.searchOptions', 'Search'),
            disabled: accounts.length <= 1,
            dataAttributes,
        });
    };

    const resetPackageSelectionState = ({preserveCategoryLocation = false} = {}) => {
        if (!preserveCategoryLocation) {
            state.selectedCategoryId = '';
            state.selectedZoneId = '';
            state.selectedLocationLabel = '';
        }
        state.selectedPaymentMethod = 'account';
        state.selectedPacketKey = '';
        state.preview = null;
        state.expandedPackageSizeKeys = [];
        state.mode = 'purchase';
        state.maxPriceAmount = '';
    };

    const openBoughtPacketCategoriesModal = (labels = []) => {
        const modalEl = document.getElementById('olxBoughtPacketCategoriesModal');
        if (!modalEl) {
            return;
        }
        const safeLabels = labels.filter(Boolean).map(String);
        const body = $(modalEl).find('[data-bought-packet-categories-modal-body]');
        body.html(safeLabels.length ? `
            <div class="olx-bought-packet-categories-list">
                ${safeLabels.map((label) => `
                    <div class="olx-bought-packet-categories-list__item">${escapeHtml(label)}</div>
                `).join('')}
            </div>
        ` : `
            <div class="olx-placement-empty">
                ${escapeHtml(t('ads:placementAccounts.categoryNotSpecified', 'Category not specified'))}
            </div>
        `);
        $(modalEl).data('restore-packages-modal', true);
        bootstrap.Modal.getOrCreateInstance(document.getElementById('olxAccountPackagesModal')).hide();
        bootstrap.Modal.getOrCreateInstance(modalEl).show();
    };

    const normalizeResourceFetchError = (error) => {
        if (error?.name === 'AbortError') {
            return t(
                'ads:placementAccounts.resourceLoadTimeout',
                'OLX resources took too long to load. Try again.',
            );
        }
        return formatErrors(error) || String(error);
    };

    const setPackageAlert = (message, type = 'danger') => {
        const alert = $('[data-account-packages-alert]');
        if (!message) {
            alert.attr('hidden', true).removeClass('alert alert-danger alert-success alert-warning').text('');
            return;
        }
        alert
            .removeAttr('hidden')
            .attr('class', `olx-account-packages-alert alert alert-${type}`)
            .text(message);
    };

    const setPurchaseAlert = (message, type = 'danger') => {
        const alert = $('[data-account-package-purchase-alert]');
        if (!message) {
            alert.attr('hidden', true).removeClass('alert alert-danger alert-success alert-warning').text('');
            return;
        }
        alert
            .removeAttr('hidden')
            .attr('class', `olx-account-packages-alert alert alert-${type}`)
            .text(message);
    };

    const updateAccountBalance = (accountId, resources = null) => {
        const target = $(`[data-account-balance="${accountId}"] span`);
        if (!target.length) {
            return;
        }
        const errors = resources?.errors || {};
        if (resources?.balance) {
            target.text(formatMoney(resources.balance)).removeAttr('title');
            return;
        }
        if (errors.balance?.length) {
            target.text('—').attr('title', errors.balance.join(', '));
            return;
        }
        target.text(t('common:loading', 'Loading')).removeAttr('title');
    };

    const loadAccountBalance = async (accountId, {force = false} = {}) => {
        const accountKey = String(accountId || '');
        if (!accountKey) {
            return null;
        }
        if (!force && state.balanceByAccountId.has(accountKey)) {
            return state.balanceByAccountId.get(accountKey);
        }
        if (!force && state.balancePromisesByAccountId.has(accountKey)) {
            return state.balancePromisesByAccountId.get(accountKey);
        }
        if (force) {
            state.balanceByAccountId.delete(accountKey);
        }
        updateAccountBalance(accountKey);
        const timeout = createRequestTimeout();
        const balancePromise = (async () => {
            try {
                const resources = await fullFetch({
                    url: urls.accountPlacementBalance(accountKey),
                    headers: getRequestHeaders(),
                    signal: timeout.signal,
                });
                state.balanceByAccountId.set(accountKey, resources);
                updateAccountBalance(accountKey, resources);
                return resources;
            } catch (error) {
                const message = normalizeResourceFetchError(error);
                updateAccountBalance(accountKey, {errors: {balance: [message]}});
                throw error;
            } finally {
                timeout.clear();
            }
        })();
        state.balancePromisesByAccountId.set(accountKey, balancePromise);
        try {
            return await balancePromise;
        } finally {
            if (state.balancePromisesByAccountId.get(accountKey) === balancePromise) {
                state.balancePromisesByAccountId.delete(accountKey);
            }
        }
    };

    const loadAccountResources = async (accountId, {force = false} = {}) => {
        const accountKey = String(accountId || '');
        if (!accountKey) {
            return null;
        }
        if (!force && state.resourcesByAccountId.has(accountKey)) {
            return state.resourcesByAccountId.get(accountKey);
        }
        if (!force && state.resourcePromisesByAccountId.has(accountKey)) {
            return state.resourcePromisesByAccountId.get(accountKey);
        }
        if (force) {
            state.resourcesByAccountId.delete(accountKey);
        }
        state.resourceErrorsByAccountId.delete(accountKey);
        state.resourcesLoading.add(accountKey);
        const timeout = createRequestTimeout();
        const resourcePromise = (async () => {
            try {
                const resources = await fullFetch({
                    url: urls.accountPlacementResources(accountKey),
                    headers: getRequestHeaders(),
                    signal: timeout.signal,
                });
                syncDelayedOperationQuotaFromPayload(resources, accountKey);
                state.resourcesByAccountId.set(accountKey, resources);
                state.balanceByAccountId.set(accountKey, resources);
                state.resourceErrorsByAccountId.delete(accountKey);
                updateAccountBalance(accountKey, resources);
                return resources;
            } finally {
                timeout.clear();
            }
        })();
        state.resourcePromisesByAccountId.set(accountKey, resourcePromise);
        try {
            return await resourcePromise;
        } catch (error) {
            state.resourceErrorsByAccountId.set(accountKey, normalizeResourceFetchError(error));
            throw error;
        } finally {
            state.resourcesLoading.delete(accountKey);
            if (state.resourcePromisesByAccountId.get(accountKey) === resourcePromise) {
                state.resourcePromisesByAccountId.delete(accountKey);
            }
        }
    };

    const loadAccountBalanceForCollapse = (collapseElement) => {
        const collapse = $(collapseElement);
        const accountId = String(collapse.data('account-collapse') || '');
        const status = String(collapse.data('account-status') || '').toUpperCase();
        const active = String(collapse.data('account-active') || '').toLowerCase();
        if (!accountId || status !== 'WORKING' || active === 'false') {
            return;
        }
        loadAccountBalance(accountId).catch(() => {});
    };

    const ensureCategoriesLoaded = async () => {
        if (state.categories.length) {
            return;
        }
        if (state.categoryPromise) {
            return state.categoryPromise;
        }
        state.categoriesLoading = true;
        state.categoryPromise = (async () => {
            const payload = await fullFetch({url: urls.category});
            state.categories = normalizeCategoryPickerCategories(normalizeCollection(payload));
        })();
        try {
            return await state.categoryPromise;
        } finally {
            state.categoriesLoading = false;
            state.categoryPromise = null;
        }
    };

    const renderCategoryControl = () => {
        categoryPicker.setCategories(state.categories);
        categoryPicker.setSelectedCategoryId(state.selectedCategoryId);
        return categoryPicker.renderField({
            label: t('ads:placementAccounts.category', 'Category'),
            disabled: state.categoriesLoading || !state.categories.length,
        });
    };

    const renderPaymentMethodControl = (resources) => {
        const methods = resources?.payment_methods?.length
            ? resources.payment_methods
            : [{value: 'account', label: 'account'}];
        if (!methods.find((method) => String(method.value || '') === String(state.selectedPaymentMethod || ''))) {
            state.selectedPaymentMethod = String(methods[0]?.value || 'account');
        }
        if (methods.length <= 1) {
            return '';
        }
        return renderDraftSearchableSelect({
            id: 'olx-account-package-payment',
            label: t('ads:placementPlan.paymentMethod', 'Payment method'),
            options: methods.map((method) => ({
                value: method.value,
                label: method.label || method.value,
            })),
            selectedValue: state.selectedPaymentMethod,
            placeholder: t('ads:placementPlan.paymentMethod', 'Payment method'),
            searchPlaceholder: t('ads:draftEditor.searchOptions', 'Search'),
            dataAttributes: 'data-account-package-payment-select',
        });
    };

    const selectFirstPaymentMethodForResources = (resources) => {
        const methods = resources?.payment_methods?.length
            ? resources.payment_methods
            : [{value: 'account', label: 'account'}];
        state.selectedPaymentMethod = String(methods[0]?.value || 'account');
    };

    const renderLocationControl = () => `
        <label for="olx-account-package-location" class="draft-field-label">
            ${escapeHtml(t('ads:placementAccounts.locationZone', 'Location / zone'))}
        </label>
        <div class="draft-location-autocomplete olx-account-package-location-control">
            <input
                type="text"
                id="olx-account-package-location"
                class="draft-input draft-location-autocomplete-input"
                placeholder="${escapeHtml(t('ads:placementAccounts.locationOptional', 'Optional'))}"
                value="${escapeHtml(state.selectedLocationLabel)}"
                autocomplete="off"
                data-account-package-location
            >
            <div class="olx-account-package-location-results" data-account-package-location-results hidden></div>
        </div>
    `;

    const getCurrentPreviewCategory = () => state.preview?.category || null;

    const getSelectedPacket = () => {
        const category = getCurrentPreviewCategory();
        if (!category) {
            return null;
        }
        const packets = getReusablePackagePurchaseOptions(category);
        return packets.find((packet) => getPlacementPacketKey(category, packet) === state.selectedPacketKey)
            || getDefaultReusablePlacementPacket(category);
    };

    const getSelectedPacketPrice = (packet = getSelectedPacket()) => {
        const price = packet?.price;
        if (!price || typeof price !== 'object') {
            return null;
        }
        const amount = Number(price.amount);
        if (!Number.isFinite(amount)) {
            return null;
        }
        return {
            ...price,
            amount,
            currency: price.currency || price.currency_code || '',
        };
    };

    const getScheduleMaxPriceAmount = (packet = getSelectedPacket()) => {
        const selectedPrice = getSelectedPacketPrice(packet);
        if (!selectedPrice) {
            return '';
        }
        const currentValue = String(state.maxPriceAmount || '').trim();
        return currentValue || String(selectedPrice.amount);
    };

    const buildPackagePurchasePayload = () => {
        const category = getCurrentPreviewCategory();
        const selected = getSelectedPacket();
        if (!category || !selected) {
            return null;
        }
        const selectedPrice = (
            !selected.price_estimated && !selected.price_unavailable
                ? getSelectedPacketPrice(selected)
                : null
        );
        return {
            operation_id: state.preview?.operation_id,
            category_id: category.category_id,
            zone_id: category.zone_id || '',
            payment_method: selected.payment_method || state.selectedPaymentMethod || 'account',
            purchase_kind: selected.purchase_kind,
            size: selected.size,
            packet_id: selected.id || '',
            packet_type: selected.packet_type || selected.type || 'base',
            is_premium: !!selected.is_premium,
            max_price: selectedPrice ? {
                amount: selectedPrice.amount,
                currency: selectedPrice.currency,
            } : null,
        };
    };

    const isScheduleMode = () => state.mode === 'schedule';

    const getBuyPackageLabel = () => t('ads:placementAccounts.buyPackage', 'Buy package');

    const buildScheduleMaxPricePayload = () => {
        const selectedPrice = getSelectedPacketPrice();
        if (!selectedPrice) {
            return null;
        }
        const amount = Number(String(getScheduleMaxPriceAmount()).replace(',', '.'));
        if (!Number.isFinite(amount)) {
            return null;
        }
        return {
            amount,
            currency: selectedPrice.currency,
        };
    };

    const getScheduleValidationError = () => {
        const quotaError = getScheduleQuotaError();
        if (quotaError) {
            return quotaError;
        }
        if (!state.scheduledAt) {
            return t('ads:placementAccounts.scheduledAtRequired', 'Scheduled date is required.');
        }
        const scheduledDate = new Date(state.scheduledAt);
        if (
            Number.isNaN(scheduledDate.getTime())
            || scheduledDate <= new Date()
            || isDateTimePickerValueInPast('#olx-account-package-scheduled-at')
        ) {
            return t('ads:placementAccounts.scheduledAtRequired', 'Scheduled date is required.');
        }
        const selectedPrice = getSelectedPacketPrice();
        if (selectedPrice) {
            const amount = Number(String(getScheduleMaxPriceAmount()).replace(',', '.'));
            if (!Number.isFinite(amount) || amount < 0) {
                return t('ads:placementAccounts.maximumAtLaunch', 'Maximum at launch');
            }
        }
        return '';
    };

    const renderScheduleQuotaWarning = () => {
        const quotaError = getScheduleQuotaError();
        if (!quotaError) {
            return '';
        }
        return `
            <div class="olx-account-package-quota-warning">
                <div>${escapeHtml(quotaError)}</div>
                <div>${escapeHtml(t(
                    'ads:placementAccounts.scheduleQuotaBlockedHint',
                    'Cancel existing scheduled actions or increase your limit to schedule this purchase.',
                ))}</div>
                <div class="olx-account-package-quota-warning__actions">
                    <a href="${escapeHtml(localizeAppPageUrl(urls.scheduledActionsPage))}">
                        ${escapeHtml(t('ads:placementAccounts.openScheduledActions', 'Open scheduled actions'))}
                    </a>
                    <a href="${escapeHtml(urls.billingPage)}">
                        ${escapeHtml(t('ads:placementAccounts.openBilling', 'Open billing'))}
                    </a>
                </div>
            </div>
        `;
    };

    const getCategorySummaryLabel = (category = getCurrentPreviewCategory()) => (
        category?.category_name
        || category?.name
        || category?.label
        || category?.category_id
        || '—'
    );

    const getPacketSummaryLabel = (packet = getSelectedPacket()) => {
        if (!packet) {
            return '—';
        }
        return packet.name
            || packet.title
            || packet.label
            || `${t('ads:placementPlan.packet', 'Package')} ${packet.size || ''}`.trim();
    };

    const renderSchedulePanel = () => {
        if (!isScheduleMode()) {
            return '';
        }
        const selected = getSelectedPacket();
        const selectedPrice = getSelectedPacketPrice(selected);
        const maxPriceValue = getScheduleMaxPriceAmount(selected);
        const selectedAccount = getAvailablePlacementAccount(state.selectedAccountId);
        const category = getCurrentPreviewCategory();
        const accountLabel = selectedAccount?.name || state.selectedAccountId || '—';
        const locationLabel = state.selectedLocationLabel
            || category?.location_name
            || category?.zone_id
            || t('ads:placementAccounts.locationOptional', 'Optional');
        return `
            <div class="olx-account-package-schedule-step">
                <div class="olx-account-package-schedule-summary">
                    <div>
                        <span>${escapeHtml(t('ads:scheduledActions.account', 'Account'))}</span>
                        <strong>${escapeHtml(accountLabel)}</strong>
                    </div>
                    <div>
                        <span>${escapeHtml(t('ads:placementAccounts.category', 'Category'))}</span>
                        <strong>${escapeHtml(getCategorySummaryLabel(category))}</strong>
                    </div>
                    <div>
                        <span>${escapeHtml(t('ads:placementAccounts.locationZone', 'Location / zone'))}</span>
                        <strong>${escapeHtml(locationLabel)}</strong>
                    </div>
                    <div>
                        <span>${escapeHtml(t('ads:placementPlan.packet', 'Package'))}</span>
                        <strong>${escapeHtml(getPacketSummaryLabel(selected))}</strong>
                    </div>
                    <div>
                        <span>${escapeHtml(t('ads:placementPlan.price', 'Price'))}</span>
                        <strong>${selectedPrice ? escapeHtml(formatMoney(selectedPrice)) : '—'}</strong>
                    </div>
                </div>
                <div class="olx-account-package-schedule-card">
                    <div class="olx-account-package-schedule-field">
                        ${dateTimePickerHtml({
                            id: 'olx-account-package-scheduled-at',
                            label: t('ads:scheduledActions.scheduledAt', 'Scheduled at'),
                            placeholder: t('ads:priceUpdate.selectDateTime', 'Select date and time'),
                            className: 'olx-account-package-schedule-picker',
                            inputClass: 'olx-account-package-scheduled-at',
                            disablePast: true,
                            labels: getDatePickerLabels(),
                        })}
                    </div>
                    ${selectedPrice ? `
                        <div class="olx-account-package-schedule-field">
                            <label class="draft-field-label" for="olx-account-package-max-price">
                                ${escapeHtml(t('ads:placementAccounts.maximumAtLaunch', 'Maximum at launch'))}
                            </label>
                            <div class="olx-account-package-max-price-row">
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    id="olx-account-package-max-price"
                                    class="draft-input olx-account-package-max-price"
                                    value="${escapeHtml(maxPriceValue)}"
                                >
                                <span>${escapeHtml(selectedPrice.currency)}</span>
                            </div>
                            <div class="placement-plan-muted olx-account-package-schedule-hint">
                                ${escapeHtml(t(
                                    'ads:placementAccounts.priceGuardHint',
                                    'The package will not be purchased if OLX price is higher than this value.',
                                ))}
                            </div>
                        </div>
                    ` : `
                        <div class="placement-plan-muted olx-account-package-schedule-hint">
                            ${escapeHtml(t(
                                'ads:placementAccounts.priceUnknownAtLaunch',
                                'OLX will calculate the price at launch; price guard is unavailable.',
                            ))}
                        </div>
                    `}
                </div>
            </div>
        `;
    };

    const scrollPurchaseModalBodyToTop = () => {
        const body = document.querySelector('#olxAccountPackagePurchaseModal .modal-body');
        if (!body) {
            return;
        }
        window.requestAnimationFrame(() => {
            body.scrollTo({top: 0, behavior: 'smooth'});
        });
    };

    const syncPurchaseDatePicker = () => {
        if (!isScheduleMode()) {
            return;
        }
        const date = state.scheduledAt ? new Date(state.scheduledAt) : null;
        state.isSyncingDatePicker = true;
        setDateTimePickerValue(
            '#olx-account-package-scheduled-at',
            date && !Number.isNaN(date.getTime()) ? date : null,
            false,
        );
        state.isSyncingDatePicker = false;
    };

    const renderPackagesRetry = (message) => `
        <div class="olx-account-packages-error">
            <div>${escapeHtml(message || t('ads:placementAccounts.resourceLoadFailed', 'Could not load OLX resources.'))}</div>
            <button type="button" class="outline-button olx-account-packages-retry">
                ${escapeHtml(t('common:retry', 'Retry'))}
            </button>
        </div>
    `;

    const renderLoadingState = () => `
        <div class="olx-account-packages-loading">
            <span class="olx-account-packages-loading__spinner" aria-hidden="true"></span>
            <span>${escapeHtml(t('common:loading', 'Loading'))}</span>
        </div>
    `;

    const renderResourceWarnings = (errors = {}, excludedKeys = []) => {
        const excluded = new Set(excludedKeys);
        const messages = Object.entries(errors)
            .filter(([key]) => !excluded.has(key))
            .flatMap(([, value]) => Array.isArray(value) ? value : [value])
            .filter(Boolean);
        if (!messages.length) {
            return '';
        }
        return `
            <div class="olx-account-packages-warning">
                ${messages.map((message) => `<div>${escapeHtml(message)}</div>`).join('')}
            </div>
        `;
    };

    const renderPackageModal = () => {
        const modal = $('#olxAccountPackagesModal');
        const selectedAccount = ensureSelectedPlacementAccount();
        const accountId = String(selectedAccount?.id || '');
        const resources = state.resourcesByAccountId.get(accountId);
        const resourceError = state.resourceErrorsByAccountId.get(accountId);
        const resourcesTarget = modal.find('[data-account-packages-resources]');
        const purchaseOpen = modal.find('.olx-account-package-purchase-open');
        const accountSelect = renderAccountSelectControl({
            id: 'olx-account-packages-account',
            dataAttributes: 'data-account-package-account-select data-account-select-context="packages"',
        });

        modal.find('[data-account-packages-account-shell]').html(accountSelect);
        modal.find('[data-account-packages-account-form]').prop('hidden', !accountSelect);

        if (!accountId) {
            modal.find('#olxAccountPackagesModalLabel').text(t('ads:placementAccounts.olxPackages', 'OLX packages'));
            resourcesTarget.html(`
                <div class="olx-placement-empty">
                    ${escapeHtml(t('ads:placementAccounts.selectOlxAccountToLoad', 'Select an OLX account to view balance and packages.'))}
                </div>
            `);
            purchaseOpen.prop('disabled', true);
            return;
        }
        const accountName = resources?.account?.account_name || resources?.account?.name || selectedAccount?.name || accountId;
        modal.find('#olxAccountPackagesModalLabel').text(`${t('ads:placementAccounts.olxPackages', 'OLX packages')}: ${accountName}`);

        if (resourceError && !resources) {
            resourcesTarget.html(renderPackagesRetry(resourceError));
            purchaseOpen.prop('disabled', true);
            return;
        }
        if (!resources || state.resourcesLoading.has(accountId)) {
            resourcesTarget.html(renderLoadingState());
            purchaseOpen.prop('disabled', true);
            return;
        }

        purchaseOpen.prop('disabled', false);
        const errors = resources.errors || {};
        const boughtPacketsError = errors.bought_packets?.length ? formatErrors(errors.bought_packets) : '';
        resourcesTarget.html(`
            ${renderResourceWarnings(errors, ['bought_packets'])}
            <div class="olx-account-packages-summary">
                <div>
                    <span>${escapeHtml(t('ads:placementPlan.balanceBefore', 'Balance before'))}</span>
                    <strong>${resources.balance ? escapeHtml(formatMoney(resources.balance)) : '—'}</strong>
                </div>
                <div>
                    <span>${escapeHtml(t('ads:placementPlan.paymentMethod', 'Payment method'))}</span>
                    <strong>${escapeHtml((resources.payment_methods || []).map((method) => method.label || method.value).join(', ') || '—')}</strong>
                </div>
            </div>
            <h6 class="olx-account-packages-section-title">${escapeHtml(t('ads:placementAccounts.boughtPackages', 'Bought packages'))}</h6>
            ${boughtPacketsError ? renderPackagesRetry(boughtPacketsError) : renderBoughtPacketsList(resources.bought_packets || [])}
        `);
    };

    const renderPurchaseModal = () => {
        const modal = $('#olxAccountPackagePurchaseModal');
        const selectedAccount = ensureSelectedPlacementAccount();
        const accountId = String(selectedAccount?.id || '');
        const resources = state.resourcesByAccountId.get(accountId);
        const previewTarget = modal.find('[data-account-package-preview]');
        const schedulePanelTarget = modal.find('[data-account-package-schedule-panel]');
        const submit = modal.find('.olx-account-package-purchase-submit');
        const scheduleSubmit = modal.find('.olx-account-package-schedule-submit');
        const cancelButton = modal.find('.olx-account-package-cancel');
        const backButton = modal.find('.olx-account-package-back');
        const accountSelect = renderAccountSelectControl({
            id: 'olx-account-package-purchase-account',
            dataAttributes: 'data-account-package-account-select data-account-select-context="purchase"',
        });
        const setPurchaseFooterState = (canSubmit) => {
            const scheduleMode = isScheduleMode();
            const scheduleQuotaBlocked = Boolean(canSubmit && getScheduleQuotaError());
            const canSchedule = Boolean(canSubmit && scheduleMode && !getScheduleValidationError());
            modal.toggleClass('is-schedule-mode', scheduleMode);
            modal.find('#olxAccountPackagePurchaseModalLabel').text(scheduleMode
                ? t('ads:placementAccounts.schedulePackagePurchase', 'Schedule')
                : getBuyPackageLabel());
            cancelButton.prop('hidden', scheduleMode);
            backButton.prop('hidden', !scheduleMode);
            submit.prop('hidden', scheduleMode)
                .prop('disabled', state.purchaseLoading || state.scheduleLoading || !canSubmit)
                .text(state.purchaseLoading ? t('common:loading', 'Loading') : getBuyPackageLabel());
            scheduleSubmit
                .toggleClass('filled-button', scheduleMode)
                .toggleClass('outline-button', !scheduleMode)
                .prop('disabled', state.purchaseLoading || state.scheduleLoading || !canSubmit || scheduleQuotaBlocked || (scheduleMode && !canSchedule))
                .text(state.scheduleLoading
                    ? t('common:loading', 'Loading')
                    : t('ads:placementAccounts.schedulePackagePurchase', 'Schedule'));
            schedulePanelTarget
                .prop('hidden', !scheduleMode || !canSubmit)
                .html(scheduleMode && canSubmit ? renderSchedulePanel() : '');
            if (scheduleMode && canSubmit) {
                syncPurchaseDatePicker();
            }
        };

        modal.find('[data-account-package-account-shell]')
            .toggle(Boolean(accountSelect))
            .html(accountSelect);
        modal.find('[data-account-package-category-shell]').html(renderCategoryControl());
        modal.find('[data-account-package-location-shell]').html(renderLocationControl());
        const paymentControl = renderPaymentMethodControl(resources);
        modal.find('[data-account-package-payment-shell]')
            .toggle(Boolean(paymentControl))
            .html(paymentControl);

        modal.find('.olx-account-package-form, [data-account-package-preview]')
            .prop('hidden', isScheduleMode());

        if (!accountId) {
            state.mode = 'purchase';
            modal.find('.olx-account-package-form, [data-account-package-preview]').prop('hidden', false);
            previewTarget.html(`<span class="placement-plan-muted">${escapeHtml(t('ads:placementAccounts.selectOlxAccountToLoad', 'Select an OLX account to view balance and packages.'))}</span>`);
            setPurchaseFooterState(false);
            return;
        }
        if (!state.selectedCategoryId) {
            state.mode = 'purchase';
            modal.find('.olx-account-package-form, [data-account-package-preview]').prop('hidden', false);
            previewTarget.html(`<span class="placement-plan-muted">${escapeHtml(t('ads:placementAccounts.selectCategoryToLoadPackages', 'Select category to load OLX packages.'))}</span>`);
            setPurchaseFooterState(false);
            return;
        }
        if (state.previewLoading) {
            previewTarget.html(renderLoadingState());
            setPurchaseFooterState(false);
            return;
        }
        if (!state.preview?.category) {
            state.mode = 'purchase';
            modal.find('.olx-account-package-form, [data-account-package-preview]').prop('hidden', false);
            previewTarget.html(`<span class="placement-plan-muted">${escapeHtml(t('ads:placementAccounts.selectCategoryToLoadPackages', 'Select category to load OLX packages.'))}</span>`);
            setPurchaseFooterState(false);
            return;
        }
        const category = state.preview.category;
        const packets = getReusablePackagePurchaseOptions(category);
        const selected = getSelectedPacket();
        if (selected && !state.selectedPacketKey) {
            state.selectedPacketKey = getPlacementPacketKey(category, selected);
        }
        const selectorAccount = {
            ...(resources?.account || {}),
            id: accountId,
            account_id: accountId,
            name: resources?.account?.name || selectedAccount?.name || accountId,
            account_name: resources?.account?.account_name || selectedAccount?.name || accountId,
            balance: resources?.balance || null,
            olx_balance: resources?.balance || null,
            payment_methods: resources?.payment_methods || [],
        };
        if (!isScheduleMode()) {
            previewTarget.html(renderReusablePackageSelector({
                account: selectorAccount,
                category,
                selectedPacket: selected,
                expandedPackageSizeKeysSet: new Set(state.expandedPackageSizeKeys),
                showOptimalQuantityHint: false,
                showNearestQuantityNote: false,
                purchasePreviewMode: 'financial',
            }) + (selected ? renderScheduleQuotaWarning() : ''));
        }
        setPurchaseFooterState(Boolean(packets.length && getSelectedPacket()));
    };

    const loadPackagePreview = async ({preserveAlert = false} = {}) => {
        const accountId = String(state.selectedAccountId || '');
        if (!accountId || !state.selectedCategoryId) {
            renderPurchaseModal();
            return false;
        }
        state.previewLoading = true;
        state.preview = null;
        state.selectedPacketKey = '';
        state.expandedPackageSizeKeys = [];
        state.mode = 'purchase';
        if (!preserveAlert) {
            setPurchaseAlert('');
        }
        renderPurchaseModal();
        try {
            const payload = {
                category_id: state.selectedCategoryId,
                zone_id: state.selectedZoneId || '',
                payment_method: state.selectedPaymentMethod || 'account',
            };
            const response = await fullFetch({
                url: urls.accountPlacementPackagePreview(accountId),
                method: 'POST',
                data: JSON.stringify(payload),
                headers: {
                    'Content-Type': 'application/json',
                    ...getRequestHeaders(),
                },
            });
            state.preview = response.preview;
            const selected = getDefaultReusablePlacementPacket(response.preview?.category);
            state.selectedPacketKey = selected ? getPlacementPacketKey(response.preview.category, selected) : '';
            return true;
        } catch (error) {
            if (!preserveAlert) {
                setPurchaseAlert(formatErrors(error) || String(error), 'danger');
            }
            return false;
        } finally {
            state.previewLoading = false;
            renderPurchaseModal();
        }
    };

    const loadPackagesModalResources = async (accountId, {force = false} = {}) => {
        const accountKey = String(accountId || '');
        if (!accountKey) {
            renderPackageModal();
            return null;
        }
        if (force) {
            state.resourcesByAccountId.delete(accountKey);
            state.resourceErrorsByAccountId.delete(accountKey);
        }
        renderPackageModal();
        try {
            return await loadAccountResources(accountKey, {force});
        } catch (error) {
            return null;
        } finally {
            renderPackageModal();
        }
    };

    const selectAccountInPackagesModal = (accountId, {force = false} = {}) => {
        state.selectedAccountId = String(accountId || '');
        state.autoSelectAccount = true;
        resetPackageSelectionState();
        setPackageAlert('');
        setPurchaseAlert('');
        renderPackageModal();
        loadPackagesModalResources(state.selectedAccountId, {force});
    };

    const selectAccountInPurchaseModal = async (accountId) => {
        state.selectedAccountId = String(accountId || '');
        state.autoSelectAccount = true;
        resetPackageSelectionState({preserveCategoryLocation: true});
        state.mode = 'purchase';
        setPurchaseAlert('');
        renderPackageModal();
        renderPurchaseModal();
        if (!state.selectedAccountId) {
            return;
        }
        const resources = await loadAccountResources(state.selectedAccountId).catch((error) => {
            setPurchaseAlert(formatErrors(error) || String(error), 'danger');
            return null;
        });
        if (resources) {
            selectFirstPaymentMethodForResources(resources);
        }
        if (state.selectedCategoryId) {
            loadPackagePreview();
            return;
        }
        renderPurchaseModal();
    };

    const openPackagesModal = ({accountId = '', autoSelectAccount = true} = {}) => {
        const availableAccounts = getAvailablePlacementAccounts();
        const selectedAccount = getAvailablePlacementAccount(accountId)
            || (availableAccounts.length === 1 || autoSelectAccount ? availableAccounts[0] : null);
        state.selectedAccountId = String(selectedAccount?.id || '');
        state.autoSelectAccount = Boolean(autoSelectAccount || availableAccounts.length <= 1 || selectedAccount);
        resetPackageSelectionState();
        setPackageAlert('');
        const modalEl = document.getElementById('olxAccountPackagesModal');
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
        renderPackageModal();
        ensureCategoriesLoaded().catch((error) => setPackageAlert(formatErrors(error) || String(error), 'danger'));
        if (state.selectedAccountId) {
            loadPackagesModalResources(state.selectedAccountId);
        }
    };

    const openPurchaseModal = async () => {
        const selectedAccount = ensureSelectedPlacementAccount();
        const accountId = String(selectedAccount?.id || '');
        if (!accountId) {
            setPackageAlert(t('ads:placementAccounts.selectOlxAccountToLoad', 'Select an OLX account to view balance and packages.'), 'warning');
            return;
        }
        state.selectedAccountId = accountId;
        state.autoSelectAccount = true;
        state.selectedCategoryId = '';
        state.selectedZoneId = '';
        state.selectedLocationLabel = '';
        state.selectedPacketKey = '';
        state.preview = null;
        state.mode = 'purchase';
        state.scheduledAt = '';
        state.maxPriceAmount = '';
        setPurchaseAlert('');
        const resources = await loadAccountResources(accountId).catch((error) => {
            setPurchaseAlert(formatErrors(error) || String(error), 'danger');
            return null;
        });
        selectFirstPaymentMethodForResources(resources);
        const purchaseModalEl = document.getElementById('olxAccountPackagePurchaseModal');
        bootstrap.Modal.getOrCreateInstance(document.getElementById('olxAccountPackagesModal')).hide();
        bootstrap.Modal.getOrCreateInstance(purchaseModalEl).show();
        renderPurchaseModal();
        ensureCategoriesLoaded()
            .then(() => renderPurchaseModal())
            .catch((error) => setPurchaseAlert(formatErrors(error) || String(error), 'danger'));
    };

    const purchaseSelectedPackage = async () => {
        const accountId = String(state.selectedAccountId || '');
        const payload = buildPackagePurchasePayload();
        if (!accountId || !payload) {
            return;
        }
        state.purchaseLoading = true;
        setPurchaseAlert('');
        renderPurchaseModal();
        try {
            const response = await fullFetch({
                url: urls.accountPlacementPackagePurchase(accountId),
                method: 'POST',
                data: JSON.stringify(payload),
                headers: {
                    'Content-Type': 'application/json',
                    ...getRequestHeaders(),
                },
            });
            const refreshRequired = Boolean(
                response.refresh_required || !response.resources || !response.preview
            );
            if (response.resources) {
                state.resourcesByAccountId.set(accountId, response.resources);
                state.balanceByAccountId.set(accountId, response.resources);
                updateAccountBalance(accountId, response.resources);
            }
            if (!refreshRequired) {
                state.preview = response.preview;
                const refreshedSelected = getDefaultReusablePlacementPacket(response.preview.category);
                state.selectedPacketKey = refreshedSelected ? getPlacementPacketKey(response.preview.category, refreshedSelected) : '';
            } else {
                state.preview = null;
                state.selectedPacketKey = '';
            }
            setPackageAlert(
                refreshRequired
                    ? t(
                        'ads:placementOperation.packagePurchasedRefreshRequired',
                        'The OLX package was purchased, but updated data could not be loaded. Refresh before buying again.',
                    )
                    : t('ads:placementOperation.packagePurchased', 'OLX package was purchased and the plan was refreshed.'),
                refreshRequired ? 'warning' : 'success',
            );
            bootstrap.Modal.getOrCreateInstance(document.getElementById('olxAccountPackagePurchaseModal')).hide();
        } catch (error) {
            const purchaseError = formatErrors(error) || String(error);
            await loadPackagePreview({preserveAlert: true});
            setPurchaseAlert(purchaseError, 'danger');
        } finally {
            state.purchaseLoading = false;
            renderPurchaseModal();
        }
    };

    const scheduleSelectedPackage = async () => {
        const accountId = String(state.selectedAccountId || '');
        const payload = buildPackagePurchasePayload();
        if (!accountId || !payload) {
            return;
        }
        const validationError = getScheduleValidationError();
        if (validationError) {
            setPurchaseAlert(validationError, 'warning');
            renderPurchaseModal();
            return;
        }
        state.scheduleLoading = true;
        setPurchaseAlert('');
        renderPurchaseModal();
        try {
            const maxPrice = buildScheduleMaxPricePayload();
            const response = await fullFetch({
                url: urls.accountPlacementPackageSchedule(accountId),
                method: 'POST',
                data: JSON.stringify({
                    ...payload,
                    scheduled_at: new Date(state.scheduledAt).toISOString(),
                    ...(maxPrice ? {max_price: maxPrice} : {}),
                }),
                headers: {
                    'Content-Type': 'application/json',
                    ...getRequestHeaders(),
                },
            });
            syncDelayedOperationQuotaFromPayload(response, accountId);
            state.mode = 'purchase';
            state.scheduledAt = '';
            state.maxPriceAmount = '';
            setPackageAlert(
                t('ads:placementAccounts.packagePurchaseScheduled', 'Package purchase was scheduled.'),
                'success',
            );
            bootstrap.Modal.getOrCreateInstance(document.getElementById('olxAccountPackagePurchaseModal')).hide();
        } catch (error) {
            const quota = syncDelayedOperationQuotaFromPayload(error, accountId);
            setPurchaseAlert(formatErrors(error) || String(error), quota ? 'warning' : 'danger');
        } finally {
            state.scheduleLoading = false;
            renderPurchaseModal();
        }
    };

    const loadLocationSuggestions = async (query) => {
        const resultsTarget = $('[data-account-package-location-results]');
        if (!query || query.length < 2) {
            resultsTarget.attr('hidden', true).empty();
            return;
        }
        try {
            const response = await fullFetch({url: urls.locationAutocomplete(query, 8)});
            const items = normalizeCollection(response);
            if (!items.length) {
                resultsTarget.attr('hidden', true).empty();
                return;
            }
            resultsTarget
                .removeAttr('hidden')
                .html(items.map((item) => `
                    <button
                        type="button"
                        class="olx-account-location-option"
                        data-zone-id="${escapeHtml(item.type === 'region' ? item.id : (item.region_id || item.id || ''))}"
                        data-label="${escapeHtml(item.label || item.name || item.id || '')}"
                    >
                        <strong>${escapeHtml(item.label || item.name || item.id || '')}</strong>
                        ${item.description || item.region_name ? `<span>${escapeHtml(item.description || item.region_name)}</span>` : ''}
                    </button>
                `).join(''));
        } catch (error) {
            resultsTarget.attr('hidden', true).empty();
        }
    };

    const syncSelectedAccountIfUnavailable = (accountId) => {
        if (String(accountId || '') !== String(state.selectedAccountId || '')) {
            return;
        }
        const fallbackAccount = getAvailablePlacementAccounts()[0] || null;
        if ($('#olxAccountPackagePurchaseModal').hasClass('show')) {
            selectAccountInPurchaseModal(fallbackAccount?.id || '');
            return;
        }
        if ($('#olxAccountPackagesModal').hasClass('show')) {
            selectAccountInPackagesModal(fallbackAccount?.id || '');
        }
    };

    const bindEvents = () => {
        const body = $('body');
        categoryPicker.bind(document);
        bindDateTimePickerEvents();

        $('#olxAccountPackagePurchaseModal')
            .off('hidden.bs.modal.olxAccountPackagesModal')
            .on('hidden.bs.modal.olxAccountPackagesModal', function () {
                if (!state.selectedAccountId) {
                    return;
                }
                renderPackageModal();
                bootstrap.Modal.getOrCreateInstance(document.getElementById('olxAccountPackagesModal')).show();
            });

        $('#olxBoughtPacketCategoriesModal')
            .off('hidden.bs.modal.olxAccountPackagesModal')
            .on('hidden.bs.modal.olxAccountPackagesModal', function () {
                const modal = $(this);
                if (!modal.data('restore-packages-modal') || !state.selectedAccountId) {
                    return;
                }
                modal.removeData('restore-packages-modal');
                bootstrap.Modal.getOrCreateInstance(document.getElementById('olxAccountPackagesModal')).show();
            });

        body.off('.olxAccountPackagesModal');

        body.on('click.olxAccountPackagesModal', '.olx-account-packages-retry', function () {
            if (!state.selectedAccountId) {
                return;
            }
            loadPackagesModalResources(state.selectedAccountId, {force: true});
        });

        body.on('click.olxAccountPackagesModal', '.olx-placement-bought-packet__categories-toggle', function () {
            openBoughtPacketCategoriesModal(parseEncodedJsonArray($(this).attr('data-category-labels')));
        });

        body.on('click.olxAccountPackagesModal', '.olx-account-package-purchase-open', function () {
            openPurchaseModal();
        });

        body.on('click.olxAccountPackagesModal', '#olxAccountPackagePurchaseModal .draft-custom-select-toggle, #olxAccountPackagesModal .draft-custom-select-toggle', function () {
            const select = $(this).closest('[data-draft-custom-select]');
            const isOpen = select.hasClass('is-open');
            $(this).closest('.modal').find('[data-draft-custom-select]').removeClass('is-open')
                .find('.draft-custom-select-toggle')
                .attr('aria-expanded', 'false');
            if (!isOpen) {
                select.addClass('is-open');
                $(this).attr('aria-expanded', 'true');
                select.find('.draft-custom-select-search').trigger('focus');
            }
        });

        body.on('click.olxAccountPackagesModal', '#olxAccountPackagePurchaseModal .draft-custom-select-option, #olxAccountPackagesModal .draft-custom-select-option', function () {
            const option = $(this);
            const select = option.closest('[data-draft-custom-select]');
            const value = String(option.data('select-value') || '');
            select.removeClass('is-open')
                .find('.draft-custom-select-toggle')
                .attr('aria-expanded', 'false');
            if (select.is('[data-account-package-account-select]')) {
                const context = String(select.data('account-select-context') || 'packages');
                if (context === 'purchase') {
                    selectAccountInPurchaseModal(value);
                    return;
                }
                selectAccountInPackagesModal(value);
                return;
            }
            if (select.is('[data-account-package-category-select]')) {
                state.selectedCategoryId = value;
                state.preview = null;
                state.selectedPacketKey = '';
                state.maxPriceAmount = '';
                loadPackagePreview();
                return;
            }
            if (select.is('[data-account-package-payment-select]')) {
                state.selectedPaymentMethod = value || 'account';
                state.maxPriceAmount = '';
                loadPackagePreview();
            }
        });

        body.on('input.olxAccountPackagesModal', '#olxAccountPackagePurchaseModal .draft-custom-select-search, #olxAccountPackagesModal .draft-custom-select-search', function () {
            const query = String($(this).val() || '').trim().toLowerCase();
            const select = $(this).closest('[data-draft-custom-select]');
            select.find('.draft-custom-select-option').each(function () {
                const label = String($(this).data('select-label') || $(this).text() || '').toLowerCase();
                $(this).toggleClass('is-hidden', Boolean(query) && !label.includes(query));
            });
        });

        $(document)
            .off('click.olxAccountPackagesModal')
            .on('click.olxAccountPackagesModal', function (event) {
                if ($(event.target).closest('#olxAccountPackagePurchaseModal [data-draft-custom-select], #olxAccountPackagesModal [data-draft-custom-select]').length) {
                    return;
                }
                $('#olxAccountPackagePurchaseModal [data-draft-custom-select], #olxAccountPackagesModal [data-draft-custom-select]').removeClass('is-open')
                    .find('.draft-custom-select-toggle')
                    .attr('aria-expanded', 'false');
            });

        body.on('input.olxAccountPackagesModal', '[data-account-package-location]', function () {
            const query = String($(this).val() || '');
            state.selectedLocationLabel = query;
            if (!query.trim()) {
                state.selectedZoneId = '';
                state.maxPriceAmount = '';
                loadPackagePreview();
            }
            window.clearTimeout(state.locationTimer);
            state.locationTimer = window.setTimeout(() => loadLocationSuggestions(query.trim()), 250);
        });

        body.on('click.olxAccountPackagesModal', '.olx-account-location-option', function () {
            state.selectedZoneId = String($(this).data('zone-id') || '');
            state.selectedLocationLabel = String($(this).data('label') || '');
            state.maxPriceAmount = '';
            $('[data-account-package-location]').val(state.selectedLocationLabel);
            $('[data-account-package-location-results]').attr('hidden', true).empty();
            loadPackagePreview();
        });

        body.on('click.olxAccountPackagesModal', '#olxAccountPackagePurchaseModal .placement-plan-size-option', function () {
            const category = getCurrentPreviewCategory();
            if (!category) {
                return;
            }
            const packet = getPreferredReusablePlacementPacketForSize(
                category,
                $(this).data('placement-size'),
                state.selectedPaymentMethod,
            );
            state.selectedPacketKey = packet ? getPlacementPacketKey(category, packet) : '';
            state.maxPriceAmount = '';
            renderPurchaseModal();
        });

        body.on('click.olxAccountPackagesModal', '#olxAccountPackagePurchaseModal .placement-plan-size-toggle', function () {
            const categoryKey = String($(this).data('placement-category-key') || '');
            if (!categoryKey) {
                return;
            }
            const expandedKeys = new Set(state.expandedPackageSizeKeys);
            if (expandedKeys.has(categoryKey)) {
                expandedKeys.delete(categoryKey);
            } else {
                expandedKeys.add(categoryKey);
            }
            state.expandedPackageSizeKeys = [...expandedKeys];
            renderPurchaseModal();
        });

        body.on('click.olxAccountPackagesModal', '#olxAccountPackagePurchaseModal .placement-plan-package-card', function () {
            state.selectedPacketKey = String($(this).attr('data-placement-packet-key') || '');
            state.maxPriceAmount = '';
            renderPurchaseModal();
        });

        body.on('click.olxAccountPackagesModal', '.olx-account-package-purchase-submit', function () {
            purchaseSelectedPackage();
        });

        body.on('click.olxAccountPackagesModal', '.olx-account-package-back', function () {
            state.mode = 'purchase';
            setPurchaseAlert('');
            renderPurchaseModal();
            scrollPurchaseModalBodyToTop();
        });

        body.on('click.olxAccountPackagesModal', '.olx-account-package-schedule-submit', function () {
            if (!isScheduleMode()) {
                const quotaError = getScheduleQuotaError();
                if (quotaError) {
                    setPurchaseAlert(quotaError, 'warning');
                    renderPurchaseModal();
                    return;
                }
                state.mode = 'schedule';
                state.maxPriceAmount = '';
                setPurchaseAlert('');
                renderPurchaseModal();
                scrollPurchaseModalBodyToTop();
                return;
            }
            scheduleSelectedPackage();
        });

        body.on('change.olxAccountPackagesModal', '#olx-account-package-scheduled-at', function () {
            if (state.isSyncingDatePicker) {
                return;
            }
            state.scheduledAt = String($(this).val() || '');
            renderPurchaseModal();
        });

        body.on('input.olxAccountPackagesModal', '.olx-account-package-max-price', function () {
            state.maxPriceAmount = String($(this).val() || '');
        });
    };

    return {
        bindEvents,
        getAvailablePlacementAccounts,
        loadAccountBalance,
        loadAccountBalanceForCollapse,
        openPackagesModal,
        syncSelectedAccountIfUnavailable,
    };
};
